import React, { useState } from 'react';
import { useT } from '../i18n';

// Shared therapeutic-detail fields used by the storefront form (Create.jsx) so
// every story line collects the same inputs.
//
// Each field is controlled: it takes the current string `value` and an
// `onChange(nextValue)` callback. personalityTraits is stored as a single
// comma-separated string (the prompt builder consumes it as free text), so the
// multi-select serialises to/from that string here.

const TRAIT_SEP = '، ';

function parseTraits(str) {
  return String(str || '')
    .split(/[,،]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

// Family structure — single-choice dropdown of predefined options.
export function FamilyStructureField({ value, onChange }) {
  const t = useT();
  return (
    <div className="field">
      <span className="field-label">{t.familyStructure}</span>
      <select value={value || ''} onChange={(e) => onChange(e.target.value)}>
        <option value="">{t.familyStructureSelectPlaceholder}</option>
        {t.familyStructureOptions.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  );
}

// Personality traits — multi-select chips plus a free-text box so caregivers can
// add traits that aren't in the predefined list.
export function PersonalityTraitsField({ value, onChange }) {
  const t = useT();
  const [custom, setCustom] = useState('');
  const selected = parseTraits(value);
  const has = (tr) => selected.includes(tr);
  const commit = (arr) => onChange(arr.join(TRAIT_SEP));
  const toggle = (tr) =>
    has(tr) ? commit(selected.filter((x) => x !== tr)) : commit([...selected, tr]);

  const addCustom = () => {
    const tr = custom.trim();
    if (tr && !has(tr)) commit([...selected, tr]);
    setCustom('');
  };

  // Traits the user typed that aren't in the predefined list — render them as
  // removable chips so they're visible and can be toggled off again.
  const customSelected = selected.filter((tr) => !t.personalityTraitOptions.includes(tr));

  return (
    <div className="field">
      <span className="field-label">{t.personalityTraits}</span>
      <div className="trait-chips">
        {t.personalityTraitOptions.map((opt) => (
          <button
            key={opt}
            type="button"
            className={`trait-chip ${has(opt) ? 'active' : ''}`}
            onClick={() => toggle(opt)}
          >
            {opt}
          </button>
        ))}
        {customSelected.map((opt) => (
          <button
            key={opt}
            type="button"
            className="trait-chip active trait-chip-custom"
            onClick={() => toggle(opt)}
          >
            {opt} ✕
          </button>
        ))}
      </div>
      <div className="trait-add-row">
        <input
          type="text"
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addCustom();
            }
          }}
          placeholder={t.personalityTraitsAddCustom}
        />
        <button type="button" className="trait-add-btn" onClick={addCustom}>
          {t.personalityTraitsAddBtn}
        </button>
      </div>
    </div>
  );
}
